import { createApp } from "vue";
import { createPinia } from "pinia";
import * as Sentry from "@sentry/vue";
import App from "./App.vue";
import { API_BASE_URL } from "./api";
import i18n from "./i18n";
import router from "./router";
import "maplibre-gl/dist/maplibre-gl.css";
import "./styles.css";

// Аналитика грузится только по согласию на cookie — см. store/cookieConsent.ts.

// Только в проде и после монтирования: сбой регистрации (или отсутствие
// поддержки service worker) не должен влиять на само приложение, только
// на возможность PWA-установки.
// Сборка под подпутём (BASE_URL != "/") service worker не регистрирует: корневой
// /sw.js принадлежит основному приложению на том же домене.
if (import.meta.env.PROD && import.meta.env.BASE_URL === "/" && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
}

// Чанк с новым content-hash из последнего деплоя не существует под старым
// именем - вкладка, открытая через деплой, ловит 404 на динамический
// import() вместо реального сбоя. Один reload лечит (подтягивает свежий
// index.html); sessionStorage-флаг не даёт зациклиться, если чанк
// действительно недоступен.
window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault();
  const key = "eco_reloaded_after_preload_error";
  if (sessionStorage.getItem(key)) return;
  sessionStorage.setItem(key, "1");
  window.location.reload();
});

const app = createApp(App);

// GlitchTip (Sentry-совместимый, см. CLAUDE.local.md) - пустой DSN на dev,
// где VITE_SENTRY_DSN не задан в docker-compose.yml. attachErrorHandler:
// false - сами вызываем captureException ниже вместо автоматической
// установки app.config.errorHandler самим SDK. browserTracingIntegration +
// tracePropagationTargets на API_BASE_URL связывают клиентский запрос с его
// обработкой на бэкенде в один трейс (сквозная трассировка).
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
if (SENTRY_DSN) {
  Sentry.init({
    app,
    dsn: SENTRY_DSN,
    environment: import.meta.env.VITE_APP_ENV || "development",
    attachErrorHandler: false,
    integrations: [Sentry.browserTracingIntegration({ router })],
    tracesSampleRate: 1.0,
    tracePropagationTargets: [API_BASE_URL],
    // Разобрано в GlitchTip вживую (get_latest_event) - это не баги
    // приложения, а шум от стороннего кода, инжектящего себя в страницу:
    // мосты in-app-браузеров (Instagram/Facebook, Android WebView),
    // reader-режим Firefox, расширение MetaMask. Все - с пустым/анонимным
    // стеком без кадров приложения.
    ignoreErrors: [
      // Мост Android in-app-браузера (см. denyUrls ниже для его filename).
      "Error invoking postMessage: Java object is gone",
      // Инжект Firefox reader-режима.
      "Can't find variable: __firefox__",
      /window\.__firefox__/,
      // Расширение MetaMask, инжектит себя на каждую страницу.
      /window\.ethereum/,
      // Мосты in-app-браузеров (переходы по Instagram/Facebook-ссылкам).
      "Error invoking post: Method not found",
      "successCallback is not a function",
      // Отказ Fullscreen/Permissions API браузера (клик Fullscreen на
      // видео) - не код приложения, hasSystemFrames: false.
      "not granted",
    ],
    denyUrls: [/^iabjs:\/\//],
  });
}

app.config.errorHandler = (err, _instance, info) => {
  Sentry.captureException(err);
  console.error(err, info);
};
app.use(createPinia());
app.use(i18n);
app.use(router);
// Тестовый стенд (VITE_GIT_BRANCH задаётся только в docker-compose.test.yml):
// ветка - в заголовке вкладки, а не плашкой поверх карты. На проде и в
// обычной разработке переменной нет - заголовок остаётся «EcoWay».
const deployBranch = import.meta.env.VITE_GIT_BRANCH as string | undefined;
if (deployBranch) document.title = `EcoWay · ${deployBranch}`;

app.mount("#app");
sessionStorage.removeItem("eco_reloaded_after_preload_error");
